import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { NgxCaptureService } from 'ngx-capture';
import { DeviceDetectorService, DeviceInfo } from 'ngx-device-detector';
import { PxTooltipDirective } from '../../directives/px-tooltip/px-tooltip.directive';
import { tap } from 'rxjs/operators';
import { environment } from "../../../../../../environments/environment";
import { AuxService } from '../../../../../common/services/aux.service';
import { MessageType } from '../../constants/MessageType';
import { ApiService } from '../../services/api.service';
import { AuthService } from '../../services/auth.service';
import { NotifierService } from '../../services/notifier.service';
import { CentruiButtonComponent } from '../centrui-button/centrui-button.component';
import { CentruiIconComponent } from '../centrui-icon/centrui-icon.component';
import { CentruiInputComponent } from '../centrui-input/centrui-input.component';
import { IntroService } from '../../services/intro.service';
import { IntroTourConfig } from '../../tours/intro-tour-step';
import { Survey } from '../../../../../common/models/Survey';
import { SurveyService } from '../../../../../common/services/survey.service';

type HelpPanelStep = 'choice' | 'support' | 'tours';
@Component({
  selector: 'app-centrui-help-form',
  templateUrl: './centrui-help-form.component.html',
  styleUrls: ['./centrui-help-form.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CentruiIconComponent, CentruiInputComponent, CentruiButtonComponent, CommonModule, PxTooltipDirective, ReactiveFormsModule, FormsModule],
  animations: [
    trigger('fadeInUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(0.5rem)' }),
        animate('200ms {{ delay }}ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ], { params: { delay: 0 } }),
    ]),
  ],
})
export class CentruiHelpFormComponent implements OnInit {
  @Input() addin = false;
  @Input() theme?: string;
  @Input() runShow = false;
  @Input() hideHeader = false;
  @Input() hideLaunchButton = false;
  @Input() smallButtons = false;
  @Input() helpFormVisible = false;
  @Output() dismissed = new EventEmitter<string | undefined>();

  freshDeskHelpForm!: FormGroup;
  baseView!: string;
  subView!: string;
  projectName!: string;
  deviceData: DeviceInfo;
  description!: string;
  browser!: any;
  screenCapture!: any;
  step: HelpPanelStep = 'choice';
  tours: IntroTourConfig[] = [];
  submitted = false;
  toursScrollLocked = false;
  toursSearchQuery = '';
  toursResultsMinHeight = 0;
  activeCustomerSurvey: Survey | null = null;
  customerSurveyAutoPromptShown = false;

  get showSurveyHelpNotification(): boolean {
    return !!this.activeCustomerSurvey && this.customerSurveyAutoPromptShown;
  }

  get isLumiTheme(): boolean {
    return this.theme === 'lumi';
  }

  get skipChoiceStep(): boolean {
    return this.addin || this.isLumiTheme;
  }

  get overviewTours(): IntroTourConfig[] {
    return this.tours.filter(tour => tour.overview);
  }

  get regularTours(): IntroTourConfig[] {
    return this.tours.filter(tour => !tour.overview);
  }

  get filteredRegularTours(): IntroTourConfig[] {
    const query = this.toursSearchQuery.trim().toLowerCase();
    if (!query) {
      return this.regularTours;
    }
    return this.regularTours.filter(tour =>
      tour.title.toLowerCase().includes(query) ||
      (tour.description?.toLowerCase().includes(query) ?? false)
    );
  }

  get showTourDivider(): boolean {
    return this.overviewTours.length > 0 && this.regularTours.length > 0;
  }

  get showToursSearch(): boolean {
    return this.regularTours.length > 0;
  }

  get toursSearchIndex(): number {
    return this.overviewTours.length + (this.showTourDivider ? 1 : 0);
  }

  get tourAnimationCount(): number {
    return this.overviewTours.length
      + (this.showTourDivider ? 1 : 0)
      + (this.showToursSearch ? 1 : 0)
      + this.filteredRegularTours.length;
  }

  tourAnimationDelay(index: number): number {
    return 80 + (index * 90);
  }

  onTourAnimationEnd(index: number): void {
    if (index === this.tourAnimationCount - 1) {
      this.onToursAnimationEnd();
    }
  }

  constructor(
    private formBuilder: FormBuilder,
    private apiService: ApiService,
    private authService: AuthService,
    private router: Router,
    private deviceInfo: DeviceDetectorService,
    private captureService: NgxCaptureService,
    private notifierService: NotifierService,
    private auxService: AuxService,
    private introService: IntroService,
    private surveyService: SurveyService
  ) {
    this.deviceData = this.deviceInfo.getDeviceInfo();
    router.events.forEach((event) => {
      if (event instanceof NavigationStart) {
        this.dismiss();
      }
    }).then(r => {
      console.log('There was an error in dismissing the form on navigation detection');
    });
    this.introService.applicableConfigs$.subscribe(configs => {
      this.tours = configs;
    });
    this.activeCustomerSurvey = this.surveyService.getActiveCustomerSurvey();
    this.surveyService.$survey.subscribe(survey => {
      this.activeCustomerSurvey = survey;
    });
    this.customerSurveyAutoPromptShown = this.surveyService.hasCustomerSurveyAutoPromptShown();
    this.surveyService.$customerSurveyAutoPromptShown.subscribe(shown => {
      this.customerSurveyAutoPromptShown = shown;
    });
  }

  @ViewChild('helpForm') helpFormComponent!: ElementRef;
  @ViewChild('regularToursResults') regularToursResults?: ElementRef<HTMLElement>;
  @ViewChild('helpPopper') helpPopper?: PxTooltipDirective;
  loading = false;

  get panelTitle(): string {
    switch (this.step) {
      case 'support':
        return 'Submit Help Request';
      case 'tours':
        return 'Tours';
      default:
        return 'How can we help?';
    }
  }

  ngOnInit(): void {
    this.resetFreshDeskHelpForm();
    if (this.runShow) {
      this.show();
    }
  }

  dismiss(ticketId?: string): void {
    this.helpFormVisible = this.isLumiTheme;
    this.step = this.skipChoiceStep ? 'support' : 'choice';
    this.loading = false;
    this.submitted = !!ticketId;
    this.dismissed.emit(ticketId);
  }

  goBack(): void {
    if (this.skipChoiceStep) {
      return;
    }
    this.toursScrollLocked = false;
    this.resetToursSearch();
    this.step = 'choice';
    this.loading = false;
    this.submitted = false;
  }

  showSupport(): void {
    this.step = 'support';
    this.resetFreshDeskHelpForm();
    this.captureScreen();
  }

  showTours(): void {
    this.resetToursSearch();
    this.toursScrollLocked = true;
    this.step = 'tours';
  }

  resetToursSearch(): void {
    this.toursSearchQuery = '';
    this.toursResultsMinHeight = 0;
  }

  captureToursResultsMinHeight(): void {
    if (this.toursResultsMinHeight > 0 || !this.showToursSearch || this.toursScrollLocked) {
      return;
    }

    requestAnimationFrame(() => {
      const results = this.regularToursResults?.nativeElement;
      if (!results) {
        return;
      }
      this.toursResultsMinHeight = results.getBoundingClientRect().height;
    });
  }

  onToursAnimationEnd(): void {
    this.toursScrollLocked = false;
    this.captureToursResultsMinHeight();
  }

  launchTour(tour: IntroTourConfig): void {
    this.dismiss();
    this.introService.setIntroConfig(null);
    setTimeout(() => this.introService.setIntroConfig({ ...tour.config, tourId: tour.id }), 0);
  }

  resetFreshDeskHelpForm(): void {
    this.freshDeskHelpForm = this.formBuilder.group({
      description: ['', [Validators.required]],
    });
  }

  getCurrentView(): void {
    const route = this.router.url;
    const routeParts = route.split('/');
    this.baseView = routeParts[1];
    if (routeParts.length > 2) {
      this.subView = routeParts[2];
    }
    if (routeParts.length > 3) {
      this.projectName = routeParts[3];
    }
  }

  sendDataToFreshDesk(): void {
    if (this.authService.userObject) {
      const formData = new FormData();
      const data = {
        description: this.freshDeskHelpForm.get('description')!.value,
        subject: environment.appName + ': Support Request',
        name: this.authService.userObject.name,
        group_id: 103000101271,
        email: this.authService.userObject.email,
        priority: 1,
        status: 2,
        'cc_emails[]': this.authService.userObject.email,
        source: 2,
        'custom_fields[cf_product]': environment.appName.toLocaleUpperCase(),
        'custom_fields[cf_user_id]': this.authService.userObject.id,
        'custom_fields[cf_user_name]': this.authService.userObject.name,
        'custom_fields[cf_os]': this.deviceData.os ? this.deviceData.os : 'Not Available',
        'custom_fields[cf_os_version]': this.deviceData.os_version ? this.deviceData.os_version : 'Not Available',
        'custom_fields[cf_browser]': this.deviceData.browser ? this.deviceData.browser : 'Not Available',
        'custom_fields[cf_browser_version]': this.deviceData.browser_version ? this.deviceData.browser_version : 'Not Available',
        'custom_fields[cf_base_view]': this.baseView,
        'custom_fields[cf_sub_view]': this.subView ? this.subView : '',
        'custom_fields[cf_content_name]': this.projectName ? this.projectName : '',
        'custom_fields[cf_browser_width]': window.innerWidth,
        'custom_fields[cf_browser_height]': window.innerHeight,
        'custom_fields[cf_zoom_ratio]': window.devicePixelRatio
      };
      for (const key in data) {
        formData.append(key, data[key as keyof typeof data]);
      }
      formData.append('attachments[]', this.screenCapture);

      this.loading = true;
      this.submitTicket(formData).subscribe((response: { id?: number | string }) => {
        const ticketId = response?.id != null ? String(response.id) : undefined;
        this.resetFreshDeskHelpForm();
        this.dismiss(ticketId);
        if (!this.isLumiTheme) {
          this.notifierService.notify(MessageType.SUCCESS, 'Your support request has been received, an agent will reply as soon as possible.');
        }
        this.loading = false;
      }, () => {
        this.loading = false;
        this.notifierService.notify(MessageType.ERROR, 'There has been an issue sending your request, please try again');
      });
    }
  }

  show(): void {
    this.submitted = false;
    this.loading = false;
    this.helpFormVisible = true;
    if (this.skipChoiceStep) {
      this.showSupport();
    } else {
      this.step = 'choice';
    }
  }

  captureScreen(): void {
    this.loading = true;
    this.getCurrentView();
    const mainWrap = this.addin ? document.getElementById('powerPointAddIn') : document.getElementById('mainWrap');
    if (mainWrap) {
      this.captureService.getImage(mainWrap, true).pipe(
        tap((img: any) => {
          fetch(img).then(res => res.blob()).then(blob => {
            this.screenCapture = new File([blob], 'screenCapture.png', { type: 'image/png' });
          });
        })
      ).subscribe(() => {
        this.loading = false;
      });
    } else {
      this.loading = false;
    }
  }

  toggleHelpForm(): void {
    if (this.helpFormVisible) {
      this.dismiss();
    } else {
      this.helpPopper?.hide();
      this.show();
    }
  }

  submitTicket(data: FormData): any {
    return this.auxService.freshDesk(data);
  }

  launchCustomerSurvey(): void {
    this.dismiss();
    this.surveyService.launchCustomerSurveyImmediately();
  }

}
